import CelebrationIcon from '@material-design-icons/svg/filled/celebration.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import FiberManualRecordIcon from '@material-design-icons/svg/filled/fiber_manual_record.svg?react';
import NotificationsIcon from '@material-design-icons/svg/filled/notifications.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useAlertActions, useAlerts } from '../../contexts/AlertContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DashboardHeader } from './DashboardHeader';

interface AlertItem {
  id: string;
  type: 'review' | 'milestone' | 'error';
  title: string;
  description: string;
  timestamp: string;
  isRead: boolean;
  actionUrl?: string | null;
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'auto',
});

function formatRelativeTime(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  if (!Number.isFinite(diffSeconds)) {
    return 'Unknown';
  }

  if (Math.abs(diffSeconds) < 60) {
    return relativeTimeFormatter.format(diffSeconds, 'second');
  }

  const diffMinutes = Math.round(diffSeconds / 60);
  if (Math.abs(diffMinutes) < 60) {
    return relativeTimeFormatter.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return relativeTimeFormatter.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30) {
    return relativeTimeFormatter.format(diffDays, 'day');
  }

  const diffMonths = Math.round(diffDays / 30);
  if (Math.abs(diffMonths) < 12) {
    return relativeTimeFormatter.format(diffMonths, 'month');
  }

  return relativeTimeFormatter.format(Math.round(diffDays / 365), 'year');
}

// Map backend alert types to frontend display types
const mapAlertType = (
  backendType: string,
): 'review' | 'milestone' | 'error' => {
  const typeMap: Record<string, 'review' | 'milestone' | 'error'> = {
    // KYB types
    KybSubmitted: 'milestone',
    KybUnderReview: 'review',
    KybApproved: 'milestone',
    KybRejected: 'error',
    KybRequiresAdditionalInfo: 'error',
    // Dapp types
    DappSubmittedForReview: 'review',
    DappApproved: 'milestone',
    DappRejected: 'error',
    DappPublished: 'milestone',
    // Release types
    ReleaseProcessingComplete: 'milestone',
    ReleaseProcessingFailed: 'error',
    ReleaseSubmittedForReview: 'review',
    ReleaseApproved: 'milestone',
    ReleaseRejected: 'error',
    ReleasePublished: 'milestone',
    // Account types
    EmailVerified: 'milestone',
    AccountApproved: 'milestone',
    AccountSuspended: 'error',
  };
  return typeMap[backendType] || 'review';
};

const AlertIcon = ({ type }: { type: AlertItem['type'] }) => {
  const iconClass = 'w-6 h-6';

  switch (type) {
    case 'review':
      return (
        <div className="size-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center">
          <PersonIcon className={`${iconClass} fill-white`} />
        </div>
      );
    case 'milestone':
      return (
        <div className="size-8 rounded-[10.67px] flex items-center justify-center">
          <CelebrationIcon className={`${iconClass} fill-orange-400`} />
        </div>
      );
    case 'error':
      return (
        <div className="size-8 rounded-[10.67px] flex items-center justify-center">
          <ErrorIcon className={`${iconClass} fill-red-500`} />
        </div>
      );
  }
};

const AlertItemComponent = ({
  alert,
  onMarkAsRead,
}: {
  alert: AlertItem;
  onMarkAsRead: (id: string) => void;
}) => {
  const openAlertAction = () => {
    if (!alert.isRead) {
      onMarkAsRead(alert.id);
    }
    if (alert.actionUrl) {
      window.location.href = alert.actionUrl;
    }
  };

  return (
    <div
      className={`rounded-lg p-3 flex items-start justify-between gap-4 cursor-pointer hover:bg-neutral-700 transition-colors ${
        alert.isRead ? 'bg-neutral-800' : ''
      }`}
      onClick={openAlertAction}
    >
      <div className="flex items-start gap-4 flex-1">
        <AlertIcon type={alert.type} />
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex flex-col">
            <h3 className="text-white font-bold text-sm leading-5">
              {alert.title}
            </h3>
            <p className="text-neutral-400 text-sm leading-5 line-clamp-1">
              {alert.description}
            </p>
          </div>
          <span className="text-neutral-400 text-xs leading-4">
            {alert.timestamp}
          </span>
        </div>
      </div>
      {!alert.isRead && (
        <FiberManualRecordIcon className="size-4 fill-blue-500 flex-shrink-0" />
      )}
    </div>
  );
};

export const DashboardAlerts = () => {
  usePageTitle('Alerts | Publishing Portal');
  const {
    alerts: contextAlerts,
    totalAlerts,
    isLoading,
    refetchAlerts,
  } = useAlerts();
  const { markAsRead, markAllAsRead } = useAlertActions();
  const [offset, setOffset] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const limit = 20;

  const alerts: AlertItem[] = contextAlerts
    .slice(offset, offset + limit)
    .map((alert) => ({
      id: alert.id,
      type: mapAlertType(alert.type),
      title: alert.title,
      description: alert.message,
      timestamp: formatRelativeTime(alert.created),
      isRead: !!alert.readAt,
      actionUrl: alert.actionUrl,
    }));

  const total = totalAlerts;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchAlerts();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Automatically mark all alerts as read after viewing the page
  useEffect(() => {
    const hasUnreadAlerts = alerts.some((alert) => !alert.isRead);

    if (hasUnreadAlerts && !isLoading) {
      // This allows users to see which alerts were unread
      const timer = setTimeout(async () => {
        await markAllAsRead();
      }, 2000); // 2 second delay to let users see unread indicators

      return () => clearTimeout(timer);
    }
  }, [alerts, isLoading, markAllAsRead]);

  const handleMarkAsRead = async (alertId: string) => {
    await markAsRead(alertId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        icon={<NotificationsIcon className="size-4 fill-white" />}
        title="Alerts"
        action={
          <div className="flex items-center gap-3">
            <motion.button
              onClick={handleRefresh}
              disabled={isRefreshing}
              whileTap={{ scale: 0.95 }}
              animate={{ rotate: isRefreshing ? 360 : 0 }}
              transition={{
                duration: isRefreshing ? 1 : 0,
                repeat: isRefreshing ? Infinity : 0,
              }}
              className="p-2 text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
              title="Refresh alerts"
            >
              <RefreshIcon className="size-4 fill-current" />
            </motion.button>
            {alerts.some((a) => !a.isRead) && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="text-sm text-neutral-400 hover:text-white transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
        }
      />

      <div className="flex-1 bg-[#161717] rounded-b-xl overflow-auto">
        <div className="max-w-[640px] mx-auto px-4 py-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="text-neutral-400">Loading alerts…</div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <NotificationsIcon className="size-12 fill-neutral-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                No alerts yet
              </h3>
              <p className="text-neutral-400">
                You'll see notifications here when there are updates about your
                apps and account.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <AnimatePresence mode="popLayout">
                {alerts.map((alert, index) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <AlertItemComponent
                      alert={alert}
                      onMarkAsRead={handleMarkAsRead}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
              {total > limit && (
                <div className="flex justify-center gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-4 py-2 text-sm text-neutral-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-neutral-400">
                    {offset + 1}-{Math.min(offset + limit, total)} of {total}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOffset((prev) => prev + limit)}
                    disabled={offset + limit >= total}
                    className="px-4 py-2 text-sm text-neutral-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
